import { sha256Hex, type EpochMillis } from '@luacentrix/shared';
import type { JsonObject, JsonValue } from '@luacentrix/contracts';

export const GENESIS_HASH = '0'.repeat(64);

export interface ChainEntry {
  readonly seq: number;
  readonly prev_hash: string;
  readonly hash: string;
  readonly payload: JsonObject;
  readonly created_at: EpochMillis;
}

export interface ChainVerifyResult {
  readonly ok: boolean;
  readonly broken_at_seq: number | null;
}

function canonical(value: JsonValue): string {
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : 'null';
  }
  if (typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(item => canonical(item)).join(',')}]`;
  }
  const record: Record<string, JsonValue> = value;
  const body = Object.keys(record)
    .sort()
    .map(key => `${JSON.stringify(key)}:${canonical(record[key] ?? null)}`)
    .join(',');
  return `{${body}}`;
}

async function hashEntry(seq: number, prevHash: string, payload: JsonObject): Promise<string> {
  return sha256Hex(`${seq}|${prevHash}|${canonical(payload)}`);
}

export async function chainGenesis(createdAt: EpochMillis = 0): Promise<ChainEntry> {
  const payload: JsonObject = {};
  const hash = await hashEntry(0, GENESIS_HASH, payload);
  return { seq: 0, prev_hash: GENESIS_HASH, hash, payload, created_at: createdAt };
}

export async function chainEntry(
  prevHash: string,
  payload: JsonObject,
  seq = 0,
  createdAt: EpochMillis = 0,
): Promise<ChainEntry> {
  const copy: JsonObject = { ...payload };
  const hash = await hashEntry(seq, prevHash, copy);
  return { seq, prev_hash: prevHash, hash, payload: copy, created_at: createdAt };
}

export async function appendChainEntry(
  previous: ChainEntry,
  payload: JsonObject,
  createdAt: EpochMillis = 0,
): Promise<ChainEntry> {
  return chainEntry(previous.hash, payload, previous.seq + 1, createdAt);
}

export async function verifyChain(entries: readonly ChainEntry[]): Promise<ChainVerifyResult> {
  let expectedSeq = 0;
  let expectedPrevHash = GENESIS_HASH;
  for (const entry of entries) {
    if (entry.seq !== expectedSeq) {
      return { ok: false, broken_at_seq: entry.seq };
    }
    if (entry.prev_hash !== expectedPrevHash) {
      return { ok: false, broken_at_seq: entry.seq };
    }
    const recomputed = await hashEntry(entry.seq, entry.prev_hash, entry.payload);
    if (recomputed !== entry.hash) {
      return { ok: false, broken_at_seq: entry.seq };
    }
    expectedSeq = entry.seq + 1;
    expectedPrevHash = entry.hash;
  }
  return { ok: true, broken_at_seq: null };
}
